import { useOne, useTranslate, useUpdate } from '@refinedev/core';
import { useForm } from '@refinedev/antd';
import { useWatch } from 'antd/es/form/Form';
import { useEffect, useState } from 'react';
import { Card } from 'antd';

import { useRulesContext } from 'contexts/rulesContext/RulesContext';
import apiEndpoint from 'core/common/apiEndPoints';
import constants from 'core/constants/Constants';
import openNotificationWithIcon from 'core/utils/notification';
import Loading from 'components/loading/loading';
import { useRulesCreateState } from 'pages/rules/context/RulesCreateStateContext';
// prettier-ignore
import { getDateRange, getDmnXmlString, getUnifiedList, onSelectRuleGroupfn, ruleGroupApiResponse } from 'pages/rules/ruleApiResponse';
import saveRule from 'pages/rules/utils/saveRule';
import getQueryBuilderError from 'pages/rules/utils/getQueryBuilderError';
// prettier-ignore
import { transformInitialRuleData, transformNewVersionRuleData } from 'pages/rules/utils/stateTransformUtils';
import { useTokenContext } from 'contexts/resource/ResourceContext';
import validateRule from 'pages/ruleChange/utils/validateRule';
import dayjs from 'dayjs';

import RuleTabsForm from 'pages/ruleChange/rule/RuleFormTabs';
import RuleChangeVersionModal from 'pages/ruleChange/rule/RuleVersionModal';
import usePermission from 'core/utils/usePermission';
import PrivateRouteConst from 'core/common/privateRouteConstant';

const RuleForm: React.FC<{
  type: string;
  id?: string;
  groupId?: string;
  pageType?: string;
  onDone?: () => void;
}> = ({ type, id, groupId, pageType, onDone }) => {
  const t = useTranslate();
  const { userDetails } = useTokenContext();
  const { refetchRuleDetails, setRefetchRuleDetails, rulePlanDataObj } =
    useRulesContext();
  const { mutate } = useUpdate();
  const state = useRulesCreateState();
  const [saveLoading, setSaveLoading] = useState(false);
  const checkPermission = usePermission();
  const hasApplicabilityView = !!checkPermission(
    PrivateRouteConst.RULE_APPLICABILITY_VIEW
  );
  const hasFormulaView = !!checkPermission(
    PrivateRouteConst.OUTPUT_FORMULA_VIEW
  );

  const { onFinish, formProps, saveButtonProps, form } = useForm({
    resource:
      pageType === 'Show' && rulePlanDataObj?.view_unique_id
        ? apiEndpoint.rules
        : apiEndpoint.rulesDraft,
    action: 'create',
    successNotification: false,
    errorNotification: false,
    redirect: false,
    queryOptions: { enabled: false },
    onMutationSuccess: (data) => {
      const message = data?.data?.message || t('notifications.createSuccess');
      openNotificationWithIcon(
        constants.success,
        t('notifications.mutationSuccess'),
        message
      );
      setRefetchRuleDetails(!refetchRuleDetails);
      setSaveLoading(false);
      onDone?.();
    },
    onMutationError(error) {
      const message = error?.message || t('notifications.createError');
      openNotificationWithIcon(
        constants.error,
        t('notifications.mutationError'),
        message
      );
      setSaveLoading(false);
    },
  });

  // prettier-ignore
  const ruleApplicabilty = useWatch('rule_expression', { form, preserve: true });
  // prettier-ignore
  const ruleApplicabilityModeType = useWatch('rule_applicability_mode_type', { form, preserve: true });
  const outputformula = useWatch('output_formula', { form, preserve: true });
  // prettier-ignore
  const outputFormulaModeType = useWatch('output_formula_mode_type', { form, preserve: true });
  const ruleDate = useWatch('rules_date', { form, preserve: true });

  // Clawback: auto-set fields when a PARTIAL clawback type is chosen
  const clawbackTypeVal = useWatch('clawback_type', { form, preserve: true });
  useEffect(() => {
    if (clawbackTypeVal === 'PARTIAL') {
      setTimeout(() => {
        form.setFieldsValue({
          output_formula_type: 'simple',
          output_formula_editor_type: 'wizard',
          output_formula_mode_type: 'single',
          rule_file_type: 'dmn',
          file_upload_type: 'generate',
        });
      }, 100);
    }
  }, [clawbackTypeVal]);

  const { ruleGroupList, isFetching: ruleGroupFetching } =
    ruleGroupApiResponse(type);
  const { data: ruleGroupData, isFetching } = useOne({
    resource:
      pageType === 'Show' && rulePlanDataObj?.view_unique_id
        ? apiEndpoint.rules
        : apiEndpoint.rulesDraft,
    id: state.editId,
    queryOptions: { enabled: state.allowEdit },
  });
  const ruleData = ruleGroupData?.data?.results;
  const uniqueId = useWatch('unique_id', { form, preserve: true });

  // DMN XML fetch — required for Tab 3 (Payout Matrix) to populate Input/Output Columns.
  const newruleVersion = useWatch('rule_version', { form, preserve: true });
  const currentRuleFileType = state?.ruleData?.filter(
    (item) => item?.rule_version === newruleVersion
  )[0]?.rule_file_type;

  const { dmnXmlList } = getDmnXmlString(
    uniqueId,
    currentRuleFileType,
    pageType
  );
  useEffect(() => {
    if (dmnXmlList?.xml_string && uniqueId) {
      state.setXmlStructure(dmnXmlList.xml_string);
      state.setPreviousXmlValues(dmnXmlList.xml_string);
    }
  }, [dmnXmlList?.xml_string, uniqueId]);

  useEffect(() => {
    if (type === 'Edit' || type === 'Show') {
      state.setAllowEdit(true);
      state.setEditId(id);
      state.setPageType(type);
    }
  }, [type]);

  const ruleGroupId = useWatch('rule_group', { form, preserve: true });
  const ruleType =
    ruleGroupList?.find((g) => g?.unique_id === ruleGroupId)?.rule_type || '';
  // prettier-ignore
  const ruleGroupDate = ruleGroupId ? getDateRange(ruleGroupList, ruleGroupId) : [];
  const unifiedListUnfiltered = getUnifiedList({ filterType: 'unfiltered' });

  function onSelectRuleGroup(e) {
    onSelectRuleGroupfn(e, ruleGroupList, form, state.allowEdit, ruleData);
  }
  useEffect(() => {
    if (ruleGroupList?.length > 0) {
      state.setRuleGroupList(ruleGroupList);
      if (ruleGroupList?.length === 1) {
        const value = ruleGroupList?.[0]?.unique_id;
        form.setFieldValue('rule_group', value);
        onSelectRuleGroup(value);
      } else if (groupId) {
        form.setFieldValue('rule_group', groupId);
        onSelectRuleGroup(groupId);
      }
    }
  }, [ruleGroupList]);

  // auto-fill the rule's date range from that group's dates
  useEffect(() => {
    if (ruleGroupList?.length > 1 && ruleGroupId && type === 'Create') {
      const selectedRuleGroupObj = ruleGroupList?.filter(
        (item) => item?.unique_id === ruleGroupId
      )[0];
      const startDate = dayjs(selectedRuleGroupObj?.rule_group_start_date);
      const endDate = dayjs(selectedRuleGroupObj?.rule_group_end_date);
      form.setFieldsValue({
        rules_date: [startDate, endDate],
      });
    }
  }, [ruleGroupId]);

  useEffect(() => {
    if (ruleData?.length > 0) {
      const updatedRuleData = ruleData.map((item) => {
        if (
          item?.rule_applicability?.query === '*' &&
          item?.output_formula?.query === '*'
        ) {
          return {
            ...item,
            rule_group_configuration_status: false,
            enter_amount: item?.output_formula?.value,
          };
        }
        return item;
      });
      const transformedState = transformInitialRuleData(updatedRuleData);
      if (transformedState) {
        form.setFieldsValue(transformedState.formValues);
        state.setInputSourceFileData(transformedState.inputSourceFileData);
        state.setPreviousValues(transformedState.previousValues);
      }
      state.setRuleData(updatedRuleData);
    }
  }, [ruleData]);
  useEffect(() => {
    if (newruleVersion) {
      const transformedState = transformNewVersionRuleData(
        ruleData,
        newruleVersion
      );
      if (transformedState) {
        form.setFieldsValue(transformedState.formValues);
        state.setInputSourceFileData(transformedState.inputSourceFileData);
        state.setPreviousValues(transformedState.previousValues);
      }
    }
  }, [newruleVersion]);

  useEffect(() => {
    state.setRuleApplicabilityError(
      getQueryBuilderError(ruleApplicabilty, ruleApplicabilityModeType)
    );
    if (constants.payoutTypes.includes(outputformula?.type)) {
      state.setOutputFormulaError(false);
    }
  }, [ruleApplicabilty, outputformula]);

  const onFinishHandler = async (value: any) => {
    setSaveLoading(true);
    state.setIsSavedClick(true);

    const clawbackTypeValOnSave = form.getFieldValue('clawback_type');
    // prettier-ignore
    const result = validateRule({ruleType,clawbackTypeValOnSave,ruleApplicabilty,ruleApplicabilityModeType,outputformula,outputFormulaModeType,state,value, form, userDetails, hasApplicabilityView, hasFormulaView,
    });

    if (!result.isValid) {
      setSaveLoading(false);
      openNotificationWithIcon(
        constants.error,
        t('notifications.mutationError'),
        result.errorMessage
      );
      return;
    }

    const { ruleValue, changedKeys } = result;
    state.setCurrentValues(ruleValue);

    const isLatest = (state.previousValues as any)?.is_latest;
    if (
      (changedKeys?.valuesChanged || changedKeys?.dmnChanged) &&
      state.pageType !== 'Create' &&
      isLatest
    ) {
      state.setNewVersionModal(true);
      setSaveLoading(false);
    } else {
      // prettier-ignore
      await saveRule(ruleValue, 'false', state, mutate, uniqueId, form, setRefetchRuleDetails, refetchRuleDetails, onFinish, t, rulePlanDataObj, setSaveLoading, onDone);
    }
  };

  const handleVersionModalOk = () =>
    // prettier-ignore
    saveRule(state.currentValues, 'true', state, mutate, uniqueId, form, setRefetchRuleDetails, refetchRuleDetails, onFinish, t, rulePlanDataObj, setSaveLoading, onDone);

  if (isFetching || ruleGroupFetching) return <Loading />;

  return (
    <>
      <Card>
        <RuleTabsForm
          form={form}
          formProps={formProps}
          saveButtonProps={saveButtonProps}
          onFinishHandler={onFinishHandler}
          unifiedListUnfiltered={unifiedListUnfiltered}
          ruleGroupDate={ruleGroupDate}
          ruleType={ruleType}
          saveLoading={saveLoading}
          type={state.pageType}
        />
      </Card>

      <RuleChangeVersionModal
        state={state}
        saveLoading={saveLoading}
        ruleDate={ruleDate}
        onOk={handleVersionModalOk}
      />
    </>
  );
};

export default RuleForm;
